import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, FileText, ExternalLink } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useSettings } from "../hooks/useSettings";

/**
 * Toggle between "fill the form" and "direct WhatsApp".
 * Default = "form" (children rendered). When user picks WhatsApp, the form
 * collapses and a big WhatsApp CTA card slides in.
 */
export default function CtaToggle({ children, prefilled }) {
  const { t } = useLanguage();
  const settings = useSettings();
  const [mode, setMode] = useState("form");

  const wa = (settings.whatsapp_phone || "908508884865").replace(/\D/g, "");
  const msg = encodeURIComponent(prefilled || "Merhaba, hizmetleriniz hakkında bilgi almak istiyorum.");

  return (
    <div data-testid="cta-toggle">
      {/* Pill toggle */}
      <div className="inline-flex rounded-full bg-mist p-1 mb-6" role="tablist">
        <button type="button" role="tab" aria-selected={mode === "form"}
          onClick={() => setMode("form")}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors
            ${mode === "form" ? "bg-cream text-ink shadow-sm" : "text-ink/55 hover:text-ink"}`}
          data-testid="cta-toggle-form">
          <FileText className="h-3.5 w-3.5" />
          {t("cta_toggle.form", "Form Doldur")}
        </button>
        <button type="button" role="tab" aria-selected={mode === "whatsapp"}
          onClick={() => setMode("whatsapp")}
          className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold transition-colors
            ${mode === "whatsapp" ? "bg-emerald-500 text-cream shadow-sm" : "text-ink/55 hover:text-ink"}`}
          data-testid="cta-toggle-whatsapp">
          <MessageCircle className="h-3.5 w-3.5" />
          {t("cta_toggle.whatsapp", "Direkt WhatsApp")}
        </button>
      </div>

      <AnimatePresence mode="wait">
        {mode === "form" ? (
          <motion.div key="form"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}>
            {children}
          </motion.div>
        ) : (
          <motion.div key="wa"
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.22 }}
            className="rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-cream p-7 sm:p-10 text-center"
            data-testid="cta-toggle-whatsapp-panel">
            <div className="mx-auto h-14 w-14 rounded-full bg-cream/15 flex items-center justify-center mb-4">
              <MessageCircle className="h-7 w-7" />
            </div>
            <h3 className="font-display text-2xl font-bold">
              {t("cta_toggle.wa_title", "Şimdi WhatsApp'tan yaz")}
            </h3>
            <p className="mt-2 text-sm text-cream/85 max-w-sm mx-auto">
              {t("cta_toggle.wa_desc", "Form yerine doğrudan ekibimize ulaş. Mesai saatlerinde dakikalar içinde dönüş yaparız.")}
            </p>
            <a
              href={`https://wa.me/${wa}?text=${msg}`}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-cream text-emerald-700 px-6 py-3 text-sm font-bold hover:scale-[1.03] transition-transform"
              data-testid="cta-toggle-wa-link">
              {t("cta_toggle.wa_button", "WhatsApp'ı Aç")}
              <ExternalLink className="h-4 w-4" />
            </a>
            <p className="mt-4 text-xs text-cream/65">{settings.brand_phone}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
